import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Users, DollarSign, TrendingUp, Plus, Download, RefreshCw,
  Landmark, Clock, CheckCircle2, Hourglass, Target,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import * as XLSX from 'xlsx';
import { richTextToPlain } from '../components/ui/RichTextInput';
import KpiTile from '../components/dashboard/KpiTile';
import TrendChart from '../components/dashboard/TrendChart';
import StatusDonut from '../components/dashboard/StatusDonut';
import TopEventsTable from '../components/dashboard/TopEventsTable';
import RecentRegistrations from '../components/dashboard/RecentRegistrations';
import MiniSplitCard from '../components/dashboard/MiniSplitCard';
import {
  filterRegistrations, computeKpis, computeStatusCounts, computeTrend,
  computeTopEvents, computeGatewaySplit, computeCollectionSplit, computeEventStatusCounts,
} from '../utils/dashboardStats';
import { parseServerDate, serverDateMs } from '../utils/serverTime';

const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const RANGES = [{ k: '7d', t: '7D' }, { k: '30d', t: '30D' }, { k: '90d', t: '90D' }, { k: 'all', t: 'All' }];

// Date guards: a null/unparseable created_at must never crash the render or abort the export.
// parseServerDate treats the backend's naive UTC strings as UTC → local (IST) display.
const fmtExportDate = (v) => {
  const d = parseServerDate(v);
  return d ? format(d, 'yyyy-MM-dd HH:mm:ss') : '';
};
const relTime = (v) => {
  const d = parseServerDate(v);
  return d ? formatDistanceToNow(d, { addSuffix: true }) : '';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState('all');       // '7d' | '30d' | '90d' | 'all'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'completed' | 'pending' | 'failed'

  // The form_data → display-name helper that used to live here is gone: the summary endpoint
  // resolves the name server-side, so the blob it needed never reaches the browser.

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // One request for every registration the user can see, carrying only the fields charted below.
  //
  // This used to be a loop that awaited GET /campaigns/{id}/registrations once per campaign and
  // pulled the full record, form_data included. At 50 campaigns that is 50 sequential round-trips
  // — measured at ~22s from a browser in India, nearly all of it latency — to compute sums over
  // four columns. The server now does that projection, so the cost no longer scales with the
  // number of events.
  const fetchDashboardData = async () => {
    try {
      const [campaignsRes, summaryRes] = await Promise.all([
        api.get('/campaigns'),
        api.get('/dashboard/registrations-summary'),
      ]);
      setCampaigns(campaignsRes.data);

      // The splits and the top-events table key off campaign metadata, so stitch the name on
      // here rather than repeating it on every one of the thousands of summary rows.
      const nameById = Object.fromEntries(campaignsRes.data.map(c => [c.id, c.name]));
      setRegistrations(summaryRes.data.map(r => ({
        ...r,
        campaignId: r.campaign_id,
        campaignName: nameById[r.campaign_id] || '',
      })));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // The dashboard now holds only summary rows, but a spreadsheet needs every form answer.
      // Fetching the full records here — on an explicit click that already shows a spinner —
      // keeps that cost off the page load, where it was buying nothing.
      const selected = selectedCampaign === 'all'
        ? registrations
        : registrations.filter(r => r.campaignId === selectedCampaign);

      if (selected.length === 0) {
        toast.error('No registrations to export');
        setExporting(false);
        return;
      }

      const distinctCampaignIds = [...new Set(selected.map(r => r.campaignId))];
      // Only the rows the current filters actually selected survive the join below, so a
      // campaign-wide fetch never widens the export.
      const wantedIds = new Set(selected.map(r => r.id));

      const fullRows = (await Promise.all(distinctCampaignIds.map(async (cid) => {
        try {
          const res = await api.get(`/campaigns/${cid}/registrations`);
          const campaignName = campaigns.find(c => c.id === cid)?.name || '';
          return res.data
            .filter(r => wantedIds.has(r.id))
            .map(r => ({ ...r, campaignId: cid, campaignName }));
        } catch {
          return [];
        }
      }))).flat();

      // Oldest first → newest on the last row.
      const filteredRegistrations = fullRows
        .sort((a, b) => serverDateMs(a.created_at) - serverDateMs(b.created_at));

      if (filteredRegistrations.length === 0) {
        toast.error('No registrations to export');
        setExporting(false);
        return;
      }

      // Build a per-campaign field_id → label map by fetching form configs
      const fieldLabelMaps = {};
      const fieldTypeMaps = {};   // cid → { field_id: type }
      const fieldOptionMaps = {}; // cid → { field_id: { optionValue: optionLabel } }
      await Promise.all(distinctCampaignIds.map(async (cid) => {
        try {
          const res = await api.get(`/campaigns/${cid}/form`);
          const fields = res.data?.fields || [];
          const map = {};
          const types = {};
          const opts = {};
          fields.forEach(f => {
            if (f.id) map[f.id] = f.label || f.id;
            if (f.id) types[f.id] = f.type;
            // Name-type fields expose _first / _last sub-keys in form_data
            if (f.type === 'name' && f.id) {
              map[`${f.id}_first`] = `${f.label || 'Name'} (First)`;
              map[`${f.id}_last`]  = `${f.label || 'Name'} (Last)`;
            }
            // Choice fields (dropdown/radio/checkbox): map stored value → label
            if (f.id && Array.isArray(f.options) && f.options.length) {
              const om = {};
              f.options.forEach(o => {
                const v = o.value ?? o.label;
                if (v !== undefined && v !== null) om[String(v)] = o.label ?? String(v);
              });
              opts[f.id] = om;
            }
          });
          fieldLabelMaps[cid] = map;
          fieldTypeMaps[cid] = types;
          fieldOptionMaps[cid] = opts;
        } catch {
          fieldLabelMaps[cid] = {};
          fieldTypeMaps[cid] = {};
          fieldOptionMaps[cid] = {};
        }
      }));

      const prettifyKey = (k) =>
        k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ');

      const resolveLabel = (campaignId, key) => {
        const m = fieldLabelMaps[campaignId] || {};
        return m[key] || prettifyKey(key);
      };

      // Map a choice field's stored value(s) to their option label(s).
      const resolveChoice = (campaignId, key, value) => {
        const om = (fieldOptionMaps[campaignId] || {})[key];
        if (!om) return value;
        if (Array.isArray(value)) return value.map(v => om[String(v)] ?? v).join(', ');
        return om[String(value)] ?? value;
      };

      // Prepare data for Excel
      const data = filteredRegistrations.map(reg => {
        const row = {
          'Event': reg.campaignName,
          'Invoice Number': reg.invoice_number || '',
          'Registration ID': reg.id,
          'Payment Status': reg.payment_status,
          'Total Amount': reg.total_amount,
          'Payment ID': reg.payment_id || '',
          'Created At': fmtExportDate(reg.created_at),
        };

        // Add form data fields — use field labels from form_config, and map
        // choice values → option labels / rich text → plain text.
        Object.keys(reg.form_data || {}).forEach(key => {
          const label = resolveLabel(reg.campaignId, key);
          const value = reg.form_data[key];
          const type = (fieldTypeMaps[reg.campaignId] || {})[key];
          let cell;
          if (type === 'rich_text') {
            cell = richTextToPlain(value);
          } else if ((fieldOptionMaps[reg.campaignId] || {})[key]) {
            cell = resolveChoice(reg.campaignId, key, value);
          } else if (value && typeof value === 'object') {
            // Flatten object values (e.g. file upload objects, name objects)
            cell = value.url || value.name || value.full_name || JSON.stringify(value);
          } else {
            cell = value;
          }
          row[label] = cell;
        });

        return row;
      });

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Registrations');

      // Add summary sheet
      const summaryKpis = computeKpis(filteredRegistrations);
      const summaryData = [
        { Metric: 'Total Registrations', Value: summaryKpis.totalRegs },
        { Metric: 'Paid Registrations', Value: summaryKpis.paid },
        { Metric: 'Pending Registrations', Value: summaryKpis.pending },
        { Metric: 'Total Revenue', Value: `₹${summaryKpis.totalRevenue.toLocaleString()}` },
      ];
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

      // Download
      const fileName = selectedCampaign === 'all'
        ? `all_registrations_${format(new Date(), 'yyyyMMdd')}.xlsx`
        : `registrations_${campaigns.find(c => c.id === selectedCampaign)?.slug || 'campaign'}_${format(new Date(), 'yyyyMMdd')}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success('Excel exported successfully!');
    } catch (error) {
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  // Derived view-model: everything on screen recomputes from the three filters.
  const vm = useMemo(() => {
    const regs = filterRegistrations(registrations, {
      campaignId: selectedCampaign, status: statusFilter, range: dateRange,
    });
    const recent = [...regs]
      .sort((a, b) => serverDateMs(b.created_at) - serverDateMs(a.created_at))
      .slice(0, 6)
      .map((r) => ({
        id: r.id,
        name: r.name || 'Guest',   // resolved server-side; form_data no longer travels
        eventName: r.campaignName || '',
        amount: r.total_amount,
        status: r.payment_status,
        timeLabel: relTime(r.created_at),
      }));
    return {
      kpis: computeKpis(regs),
      statusCounts: computeStatusCounts(regs),
      trend: computeTrend(regs, dateRange),
      topEvents: computeTopEvents(regs, campaigns).slice(0, 5),
      gateway: computeGatewaySplit(regs, campaigns),
      collection: computeCollectionSplit(regs, campaigns),
      eventStatus: computeEventStatusCounts(
        selectedCampaign === 'all' ? campaigns : campaigns.filter((c) => c.id === selectedCampaign)
      ),
      recent,
    };
  }, [registrations, campaigns, selectedCampaign, statusFilter, dateRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-jakarta">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ background: '#f1f5f9' }}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-xs font-jakarta">Overview of all events</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Date range segmented control */}
          <div className="flex items-center rounded-lg border bg-white p-0.5" style={{ borderColor: '#e2e8f0' }}>
            {RANGES.map((r) => (
              <button
                key={r.k}
                onClick={() => setDateRange(r.k)}
                className={`text-xs px-2.5 py-1 rounded-md font-jakarta font-medium ${dateRange === r.k ? 'text-white' : 'text-slate-500'}`}
                style={dateRange === r.k ? { background: '#6d28d9' } : {}}
              >
                {r.t}
              </button>
            ))}
          </div>
          {/* Event filter */}
          <select
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            className="h-9 px-3 rounded-lg border text-sm bg-white font-jakarta focus:outline-none focus:ring-2 focus:ring-primary/40"
            style={{ borderColor: '#e2e8f0' }}
          >
            <option value="all">All Events</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 rounded-lg border text-sm bg-white font-jakarta focus:outline-none focus:ring-2 focus:ring-primary/40"
            style={{ borderColor: '#e2e8f0' }}
          >
            <option value="all">All Status</option>
            <option value="completed">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
          <Button onClick={handleExportExcel} disabled={exporting} variant="outline"
            className="border-slate-200 hover:bg-slate-50 font-jakarta rounded-lg h-9">
            <Download className="w-4 h-4 mr-1.5" />{exporting ? 'Exporting...' : 'Export'}
          </Button>
          <Button onClick={handleRefresh} disabled={refreshing} variant="outline"
            className="border-slate-200 hover:bg-slate-50 font-jakarta rounded-lg h-9">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button onClick={() => navigate('/campaigns/create')}
            className="bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta rounded-lg h-9">
            <Plus className="w-4 h-4 mr-1.5" />Create Event
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiTile icon={DollarSign} label="Total Revenue" value={inr(vm.kpis.totalRevenue)} sub="Paid registrations" accent="#16a34a" iconBg="#dcfce7" />
        <KpiTile icon={Landmark} label="Net Revenue (after 2.95%)" value={inr(vm.kpis.netRevenue)} sub="Payout to organizers" accent="#0ea5e9" iconBg="#e0f2fe" />
        <KpiTile icon={Clock} label="Pending Revenue" value={inr(vm.kpis.pendingRevenue)} sub={`${vm.kpis.pending} unpaid`} accent="#f59e0b" iconBg="#fef3c7" />
        <KpiTile icon={Users} label="Registrations" value={vm.kpis.totalRegs} sub="Total in range" accent="#6d28d9" iconBg="#ede9fe" />
        <KpiTile icon={CheckCircle2} label="Paid" value={vm.kpis.paid} sub={`of ${vm.kpis.totalRegs} total`} accent="#16a34a" iconBg="#dcfce7" />
        <KpiTile icon={Hourglass} label="Pending" value={vm.kpis.pending} sub="Awaiting payment" accent="#f59e0b" iconBg="#fef3c7" />
        <KpiTile icon={Target} label="Conversion" value={`${vm.kpis.conversion.toFixed(1)}%`} sub="Paid / total" accent="#6d28d9" iconBg="#ede9fe" />
        <KpiTile icon={TrendingUp} label="Avg. Value" value={inr(vm.kpis.avgValue)} sub="Per paid registration" accent="#0ea5e9" iconBg="#e0f2fe" />
      </div>

      {/* Trend + donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="lg:col-span-2 bg-white rounded-xl border p-4" style={{ borderColor: '#eef1f6' }}>
          <h3 className="text-[13px] font-outfit font-bold text-slate-900">Registrations &amp; Revenue</h3>
          <div className="text-[11px] text-slate-400 mb-2 font-jakarta">Bars = registrations · Line = revenue</div>
          <TrendChart data={vm.trend} />
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#eef1f6' }}>
          <h3 className="text-[13px] font-outfit font-bold text-slate-900">By Payment Status</h3>
          <div className="text-[11px] text-slate-400 mb-1 font-jakarta">{vm.kpis.totalRegs} total</div>
          <StatusDonut counts={vm.statusCounts} />
        </div>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="lg:col-span-2 bg-white rounded-xl border p-4" style={{ borderColor: '#eef1f6' }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-outfit font-bold text-slate-900">Top Events</h3>
            <button onClick={() => navigate('/campaigns')} className="text-[11px] font-jakarta" style={{ color: '#6d28d9' }}>View all →</button>
          </div>
          <TopEventsTable rows={vm.topEvents} onRowClick={(id) => navigate(`/campaigns/${id}`)} />
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#eef1f6' }}>
          <h3 className="text-[13px] font-outfit font-bold text-slate-900 mb-2">Recent Registrations</h3>
          <RecentRegistrations items={vm.recent} />
        </div>
      </div>

      {/* Bottom strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniSplitCard title="Payment Gateway" hint="revenue split" rows={[
          { label: 'Razorpay', value: inr(vm.gateway.razorpay || 0), color: '#6d28d9' },
          { label: 'Instamojo', value: inr(vm.gateway.instamojo || 0), color: '#0ea5e9' },
        ]} />
        <MiniSplitCard title="Collection Method" hint="registrations" rows={[
          { label: 'Online', value: vm.collection.online, color: '#16a34a' },
          { label: 'Offline / Cash', value: vm.collection.offline, color: '#f59e0b' },
        ]} />
        <MiniSplitCard title="Events" hint="by status" rows={[
          { label: 'Active', value: vm.eventStatus.active, color: '#16a34a' },
          { label: 'Draft', value: vm.eventStatus.draft, color: '#94a3b8' },
          { label: 'Closed', value: vm.eventStatus.closed, color: '#dc2626' },
        ]} />
      </div>
    </div>
  );
};

export default Dashboard;
